import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Helmet } from "react-helmet";
import { FaCoins } from "react-icons/fa";
import moment from "moment-timezone";
import TerminalCalendarButton from './TerminalCalendarButton';
import CertificateButton from './CertificateButton';

import ProblemSet from "./ProblemSet";

// Redux actions
import {
  getContest,
  getPointsDistribution,
  register
} from "../../features/competition/competitionSlice";
import { getUser } from "../../features/auth/authSlice";

// Custom hook imports
import useCountdown from "./hooks/useCountdown";

// Logo placeholder components - in a more subtle, compact design
const SponsorLogos = () => (
  <div className="flex flex-wrap justify-center items-center gap-4 mt-4">
    <div className="bg-white/90 px-3 py-1.5 rounded flex items-center h-8 shadow-sm">
      <div className="text-blue-700 text-xs font-bold">CompanyA</div>
    </div>
    <div className="bg-white/90 px-3 py-1.5 rounded flex items-center h-8 shadow-sm">
      <div className="text-green-600 text-xs font-bold">CompanyB</div>
    </div>
    <div className="bg-white/90 px-3 py-1.5 rounded flex items-center h-8 shadow-sm">
      <div className="text-orange-500 text-xs font-bold">CompanyC</div>
    </div>
    <div className="bg-white/90 px-3 py-1.5 rounded flex items-center h-8 shadow-sm">
      <div className="text-blue-500 text-xs font-bold">CompanyD</div>
    </div>
    <div className="bg-white/90 px-3 py-1.5 rounded flex items-center h-8 shadow-sm">
      <div className="text-purple-600 text-xs font-bold">CompanyE</div>
    </div>
  </div>
);

function CompetitionInfo() {
  const { pointsDistribution, contest, registrationStatus, generalError } =
    useSelector((state) => state.competitions);
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { tag } = useParams();
  const [accessCode, setAccessCode] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);


  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    try {
      dispatch(getContest(tag));
      dispatch(getUser());
      dispatch(getPointsDistribution("Apex Algos"));

    } catch (err) {
      console.log(err);
    }
  }, [tag, registrationStatus, dispatch]);

  let pointsJson = {};
  if (pointsDistribution?.points) {
    pointsJson = JSON.parse(pointsDistribution?.points)?.points_distribution;
  }

  /* Calendar Logic */
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  let calURL = "https://www.CampApex.org/compete/" + contest?.contest?.tag;
  const userDateTime = moment.utc(contest?.contest?.startdate).tz(timeZone);
  const startDate = userDateTime.format("YYYY-MM-DD");
  const startTime = userDateTime.format("HH:mm");
  const endDate = userDateTime.add(2, "hours").format("YYYY-MM-DD");
  const endTime = userDateTime.format("HH:mm");

  const isActive = useCountdown(contest?.contest?.startdate);

  useEffect(() => {
    if (isActive) {
      dispatch(getContest(tag)); // Re-fetch
    }
  }, [isActive, tag, dispatch]);

  function handleRegistration() {
    dispatch(
      register({ contestId: contest?.contest?._id, accessCode: accessCode })
    );
  }

  const calendarProps = contest?.contest ? {
    name: `Camp Apex ${contest.contest.name} Competition`,
    location: calURL,
    startDate: startDate,
    endDate: endDate,
    startTime: startTime,
    endTime: endTime,
    timeZone: timeZone
  } : null;
  
  return (
    <div className="min-h-screen bg-zinc-900 font-mono text-zinc-100">


<Helmet>
        <title>
          {contest?.contest?.name 
            ? `${contest.contest.name} Competition - Camp Apex`
            : "Salesforce Coding Competition - Camp Apex"}
        </title>
        <meta name="description" content="Join Camp Apex's Salesforce coding competitions. Test your Apex skills, compete with others, and win $200 USD toward your next Salesforce certification." />
        <meta name="keywords" content="salesforce, apex, coding competition, developer challenge, programming contest, camp apex" />
        <link rel="canonical" href={`https://www.campapex.org/compete/${tag}`} />
        
        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content={contest?.contest?.name 
          ? `${contest.contest.name} Competition - Camp Apex`
          : "Salesforce Coding Competition - Camp Apex"} />
        <meta property="og:description" content="Join Camp Apex's Salesforce coding competitions. Test your Apex skills, compete with others, and win $200 USD toward your next Salesforce certification." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.campapex.org/compete/${tag}`} />
        
        {/* Twitter Card data */} 
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={contest?.contest?.name 
          ? `${contest.contest.name} Competition - Camp Apex`
          : "Salesforce Coding Competition - Camp Apex"} />
        <meta name="twitter:description" content="Join Camp Apex's Salesforce coding competitions. Test your Apex skills, compete with others, and win $200 USD toward your next Salesforce certification." />
        
        {/* Structured data for rich results */}
        {contest?.contest && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Event",
              "name": `${contest.contest.name} Competition`,
              "description": contest.contest.description?.substring(0, 160) || "Join Camp Apex's Salesforce coding competitions. Test your Apex skills, compete with others, and win $200 USD toward your next Salesforce certification.",
              "startDate": contest.contest.startdate,
              "endDate": contest.contest.enddate, 
              "location": {
                "@type": "VirtualLocation",
                "url": `https://www.campapex.org/compete/${tag}`
              },
              "organizer": {
                "@type": "Organization",
                "name": "Camp Apex",
                "url": "https://www.campapex.org"
              },
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock",
                "validFrom": new Date().toISOString()
              }
            })}
          </script>
        )}
      </Helmet>

      {/* Competition Header */}
      <div className="bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 pt-6 pb-8 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-cyan-400 mb-4">
            {contest?.contest?.name} {contest?.contest?.type && `- ${contest?.contest?.type}`}
          </h1>
          
          <CompetitionStatus contest={contest} />
          
          {/* Subtle sponsor logos */}
          {/* <div className="mt-4 opacity-80 hover:opacity-100 transition-opacity">
            <SponsorLogos />
          </div> */}
        </div>
      </div>

      {/* Divider */}
      <div className="h-px bg-gradient-to-r from-lime-500/30 via-cyan-500/30 to-purple-500/30"></div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Two column layout for description and points */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="md:col-span-2 bg-zinc-800/20 rounded-lg p-5 border border-zinc-800">
            <h2 className="text-xl font-medium text-white mb-4 flex items-center">
              <span className="w-1 h-5 bg-lime-500 rounded-sm mr-2"></span>
              Contest Details
            </h2>
            <div className="text-zinc-300 prose prose-invert prose-sm max-w-none">
              <ReactMarkdown
                children={contest?.contest?.description}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              />
            </div>
          </div>
          
          <div className="bg-zinc-800/20 rounded-lg p-5 border border-zinc-800">
            <h2 className="text-xl font-medium text-white mb-4 flex items-center">
              <span className="w-1 h-5 bg-lime-500 rounded-sm mr-2"></span>
              Points
            </h2>
            <table className="w-full">
              <tbody>
                {Object.entries(pointsJson).map(([position, points], index) => (
                  <tr key={index} className="border-b border-zinc-800 last:border-0">
                    <td className="py-2 text-zinc-300">{position}</td>
                    <td className="py-2 text-right text-yellow-400 flex items-center justify-end">
                      {points} <FaCoins className="ml-2" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        

        <div className="bg-zinc-800/20 rounded-lg p-5 border border-zinc-800 mb-8 text-center">
  Prize:
  <a
    href="/competition-rules"
    target="_blank"
    className="ml-2 text-yellow-400 hover:text-yellow-600 font-medium transition-colors underline"
  >
    $ Cash Prizes Up For Grabs $
  </a>
</div> 
        {/* Registration section */}
        <div className="bg-zinc-800/20 rounded-lg p-5 border border-zinc-800 mb-8">
          <div className="text-center">
            <RegistrationSection
              registrationStatus={registrationStatus}
              contest={contest}
              user={user[0]}
              handleRegistration={handleRegistration}
              generalError={generalError?.Error}
              accessCode={accessCode}
              setAccessCode={setAccessCode}
              calendarProps={calendarProps}
            />
          </div>
          {contest?.contest?.enddate > new Date().toISOString() && (
            <div className="mt-4 flex justify-center">
              <TerminalCalendarButton
                name={`Camp Apex ${contest?.contest?.name} Competition`}
                location={calURL}
                startDate={startDate}
                endDate={endDate}
                startTime={startTime}
                endTime={endTime}
                timeZone={timeZone}
              />
            </div>
          )}
        </div>
            
        {/* Adjusted layout for problem set and contestants sections */}
        {!isMobile ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-zinc-800/20 rounded-lg p-5 border border-zinc-800">
              <h2 className="text-xl font-medium text-white mb-4 flex items-center">
                <span className="w-1 h-5 bg-lime-500 rounded-sm mr-2"></span>
                Problem Set
              </h2>
              <div className="text-zinc-300">
                <ProblemSet />
              </div>
            </div>
                
            <div className="bg-zinc-800/20 rounded-lg p-5 border border-zinc-800">
              <h2 className="text-xl font-medium text-white mb-4 flex items-center">
                <span className="w-1 h-5 bg-lime-500 rounded-sm mr-2"></span>
                Leaderboard
              </h2>
              <ContestantLeaderboard />
            </div>
          </div>
        ) : (
          /* Mobile View */
          <div className="space-y-6">
            <div className="bg-zinc-800/20 rounded-lg p-5 border border-zinc-800">
              <h2 className="text-xl font-medium text-white mb-4 flex items-center">
                <span className="w-1 h-5 bg-lime-500 rounded-sm mr-2"></span>
                Problem Set
              </h2>
              <div className="text-zinc-300">
                <ProblemSet />
              </div>
            </div>
            
            <div className="bg-zinc-800/20 rounded-lg p-5 border border-zinc-800">
              <h2 className="text-xl font-medium text-white mb-4 flex items-center">
                <span className="w-1 h-5 bg-lime-500 rounded-sm mr-2"></span>
                Leaderboard
              </h2>
              <ContestantLeaderboard />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Status component with countdown
function CompetitionStatus({ contest }) {
  const startDate = contest?.contest?.startdate;
  const endDate = contest?.contest?.enddate;
  const now = new Date().toISOString();

  const isActive = startDate < now && endDate > now;
  const isHolding = startDate > now;
  const isFinished = endDate < now;

  let activeText = contest?.registrationStatus?.status === "Finished"
    ? "You did it! Final results available in:"
    : "Happening now. Ends in:";
  let pendingText = "Contest starts in:";
  let pastText = "This contest has ended.";

  return (
    <div className="text-zinc-300 text-lg mt-2">
      {isActive && (
        <>
          <span>{activeText} </span>
          <Countdown targetUtcDate={contest?.contest?.enddate || 0} />
        </>
      )}
      {isHolding && (
        <>
          <span>{pendingText} </span>
          <Countdown targetUtcDate={contest?.contest?.startdate || 0} showDays={true} />
        </>
      )}
      {isFinished && <span className="text-zinc-400">{pastText}</span>}
    </div>
  );
}

// Countdown component
function Countdown({ targetUtcDate, showDays = false }) {
  const [timeLeft, setTimeLeft] = useState("");

  const calculateTimeLeft = useCallback(() => {
    const targetDate = new Date(targetUtcDate).getTime();
    const currentDate = new Date().getTime();
    const difference = targetDate - currentDate;

    if (difference > 0) {
      let timeLeft = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };

      if (showDays) {
        return `${timeLeft.days}d ${timeLeft.hours}h ${timeLeft.minutes}m ${timeLeft.seconds}s`;
      }

      return `${timeLeft.hours}h ${timeLeft.minutes}m ${timeLeft.seconds}s`;
    }

    return "";
  }, [targetUtcDate, showDays]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  return <span className="font-medium text-lime-400">{timeLeft}</span>;
}

function RegistrationSection({
  registrationStatus,
  contest,
  user,
  handleRegistration,
  generalError,
  accessCode,
  setAccessCode
}) {
  const startDate = contest?.contest?.startdate;
  const endDate = contest?.contest?.enddate;
  const now = new Date().toISOString();

  const isActive = startDate < now && endDate > now;
  const isHolding = startDate > now;
  const isFinished = endDate < now;

  let registrationError = registrationStatus?.Error && user?.instanceUrl;
  let registeredForUpcomingContest = contest?.registrationStatus && !contest?.challenges && isHolding;
  let mustRegister = !contest?.registrationStatus && !registrationStatus?.Error && user?.instanceUrl && !isFinished;
  let mustLogin = !user?.instanceUrl && !isFinished;
  let alreadyCompleted = contest?.registrationStatus?.status === "Finished";

  return (
    <div className="flex justify-center items-center gap-4 flex-wrap">
      {mustLogin ? (
        <Link
          to="/auth"
          target="_blank"
          className="bg-lime-600 hover:bg-lime-700 text-white py-2 px-6 rounded-md transition-colors"
        >
          Login to Register
        </Link>
      ) : registrationError ? (
        <div className="text-yellow-400">
          {registrationStatus?.Error}{" "}
          {registrationStatus?.FixRoute && (
            <Link
              to="/auth"
              target="_blank"
              className="text-lime-400 hover:underline"
            >
              {registrationStatus?.FixLabel}
            </Link>
          )}
        </div>
      ) : generalError ? (
        <div className="text-red-400">{generalError}</div>
      ) : registeredForUpcomingContest ? (
        <div className="flex flex-wrap gap-4 items-center justify-center">

        <div className="text-lime-400">
          You're registered. See you when the competition starts.
          <CertificateButton 
            contestId={contest?.contest?._id} 
            certificateType="contest-register"  
          />
        </div>
                
</div>
      ) : mustRegister ? (
        <>
          {contest?.contest?.location !== "Virtual" && (
            <input
              type="text"
              placeholder="Access code"
              className="bg-zinc-700 border border-zinc-600 text-zinc-300 px-3 py-2 rounded-md focus:outline-none focus:ring-1 focus:ring-lime-500"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
            />
          )}

          <button
            className="bg-lime-600 hover:bg-lime-700 text-white py-2 px-6 rounded-md transition-colors"
            onClick={handleRegistration}
          >
            Register
          </button>
        </>
      ) : alreadyCompleted && isActive ? (
        <div className="text-lime-400">
          Nice work! You've completed this contest.
        </div>
      ) : isActive ? (
        <div className="text-yellow-400">
          We're live. Solve these before time runs out!
        </div>
      ) : isFinished ? (
        <div className="text-yellow-400">
          This contest is over. Check out some of the upcoming ones.
        </div>
      ) : null}
    </div>
  );
}

// Contestant leaderboard
function ContestantLeaderboard() {
  const { contest } = useSelector((state) => state.competitions);
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  
  let updatedContest = contest?.contestLeaderboard?.map((item) => {
    if (item.endTime) {
      const userDateTime = moment
        .utc(item.endTime)
        .tz(timeZone)
        .format("hh:mm A");
      return {
        ...item,
        endTimeOnly: userDateTime,
      };
    }

    return {
      ...item,
    };
  });

  if (updatedContest) {
    updatedContest.forEach((item) => {
      let start = moment(item.startTime);
      let end = moment(item.endTime);

      if (item.endTime) {
        const duration = moment.duration(end.diff(start));
        let h = duration.hours();
        let m = duration.minutes();
        let s = duration.seconds();

        item.totalTime = h === 0
          ? `${m}m ${s}s`
          : `${h}h ${m}m ${s}s`;
      } else {
        item.totalTime = "--:--";
      }
    });
  }

  if (!updatedContest || updatedContest.length === 0) {
    return <div className="text-yellow-400">Contestants revealed once the competition begins</div>;
  }

  return (
    <div className="max-h-96 overflow-y-auto">
      <table className="w-full">
        <thead className="border-b border-zinc-700 sticky top-0 bg-zinc-900/90 backdrop-blur z-10">
          <tr>
            <th className="pb-2 pt-1 text-left text-lime-400 font-medium w-10">#</th>
            <th className="pb-2 pt-1 text-left text-lime-400 font-medium">Developer</th>
            <th className="pb-2 pt-1 text-left text-lime-400 font-medium">Status</th>
            <th className="pb-2 pt-1 text-right text-lime-400 font-medium">Time</th>
          </tr>
        </thead>
        <tbody>
          {updatedContest.map((item, index) => (
            <tr key={index} className="border-b border-zinc-800 last:border-0 hover:bg-zinc-800/30">
              <td className="py-2 text-zinc-400">{index + 1}</td>
              <td className="py-2 text-zinc-300">{item.displayName}</td>
              <td className="py-2">
                <span className={item.status === "Finished" ? "text-lime-400" : "text-yellow-400"}>
                  {item.status}
                </span>
              </td>
              <td className="py-2 text-right text-zinc-300">{item.totalTime}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default CompetitionInfo;